import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FC } from 'react';
// Components
import { ExpandMore, Replay, Download } from '@mui/icons-material';
import {
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormLabel,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { IACHBatchNotificationEmail } from '../../../models';
import { formatShortFriendlyDateWithTime } from '../../../helpers';

interface INotificationTableResponsiveComponent {
  original: IACHBatchNotificationEmail;
  handleResendEmail: (val: string | number) => void;
  handleDownloadPDF: (val: IACHBatchNotificationEmail) => void;
  isSending: {
    [id: string | number]: boolean;
  };
  isDownloading: {
    [id: string | number]: boolean;
  };
}

export const NotificationTableResponsiveComponent: FC<INotificationTableResponsiveComponent> = ({
  original,
  handleResendEmail,
  isSending,
  handleDownloadPDF,
  isDownloading
}) => {
  const classes = useStyles();
  const { batchNotificationEmailId, vendor, emailBody, emailSubject, sent, createdOn } = original;
  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography variant="body2">{vendor}</Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Vendor:
          </FormLabel>
          <Typography component="span" variant="body2">
            {vendor || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Email Subject:
          </FormLabel>
          <Typography component="span" variant="body2">
            {emailSubject || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Email Body:
          </FormLabel>
          <Typography component="span" variant="body2">
            {emailBody || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Queued Date:
          </FormLabel>
          <Typography component="span" variant="body2">
            {createdOn ? formatShortFriendlyDateWithTime(createdOn) : '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Sent:
          </FormLabel>
          <Typography component="span" variant="body2">
            {sent ? 'Yes' : 'No'}
          </Typography>
        </div>
        <Box
          marginTop="1rem"
          display="flex"
          flexDirection={{
            xs: 'column',
            sm: 'row'
          }}
        >
          <LoadingButton
            loading={isSending[batchNotificationEmailId]}
            loadingPosition="start"
            variant="contained"
            color="primary"
            startIcon={<Replay />}
            onClick={() => handleResendEmail(batchNotificationEmailId)}
          >
            Resend Email
          </LoadingButton>
          <Box marginLeft={{ xs: 0, sm: '8px' }} marginTop={{ xs: '8px', sm: 0 }}>
            <LoadingButton
              loading={isDownloading[batchNotificationEmailId]}
              loadingPosition="start"
              variant="contained"
              color="secondary"
              startIcon={<Download />}
              onClick={() => handleDownloadPDF(original)}
              sx={{
                width: {
                  xs: '100%',
                  sm: 'auto'
                }
              }}
            >
              PDF
            </LoadingButton>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  roleWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  editButton: {
    padding: `2px 5px`
  },
  approveButton: {
    padding: `2px 5px`
  },
  boldName: {
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    color: theme.palette.typography.labels,
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex'
  },
  activeName: {
    color: theme.palette.grey[500]
  },
  divider: {
    margin: '0 5px'
  },
  part: {
    flex: 1
  },
  statusRed: {
    color: theme.palette.error.main
  },
  tableCheckbox: {
    marginRight: 0
  }
}));
